import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MessageCircle, Send, Mail, Clock, CheckCheck, Plus, Users, X } from 'lucide-react';
import { messagesAPI, connectionAPI } from '../services/api';

const MessagesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [searchDealer, setSearchDealer] = useState('');

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    // Check if we have a userId in URL params to start a new conversation
    const userId = searchParams.get('userId');
    const userName = searchParams.get('userName');
    const businessName = searchParams.get('businessName');

    if (userId && userName) {
      // Check if conversation already exists
      const existingConv = conversations.find(c => c.user_id === parseInt(userId));
      if (existingConv) {
        setSelectedConversation(existingConv);
      } else {
        // Create a new conversation object
        setSelectedConversation({
          user_id: parseInt(userId),
          user_name: userName,
          user_business_name: businessName || null,
          last_message: '',
          last_message_time: new Date(),
          unread_count: 0,
          is_last_message_from_me: false
        });
        setMessages([]);
      }
    }
  }, [searchParams, conversations]);

  useEffect(() => {
    if (selectedConversation && selectedConversation.last_message) {
      fetchThread(selectedConversation.user_id);
    }
  }, [selectedConversation]);

  const fetchConversations = async () => {
    try {
      const response = await messagesAPI.getConversations();
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchThread = async (userId) => {
    try {
      const response = await messagesAPI.getThread(userId);
      setMessages(response.data);
      // Mark all messages in this thread as read
      await messagesAPI.markThreadAsRead(userId);
      // Refresh conversations to update unread counts
      fetchConversations();
    } catch (error) {
      console.error('Error fetching thread:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      await messagesAPI.sendMessage({
        receiver_id: selectedConversation.user_id,
        message: newMessage
      });
      setNewMessage('');
      // Refresh thread and conversations
      await fetchThread(selectedConversation.user_id);
      await fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const fetchDealers = async () => {
    try {
      const response = await connectionAPI.getMyConnections();
      // Map connections to dealer format for the modal
      const connectedDealers = (response.data.connections || []).map(conn => ({
        id: conn.dealer_id,
        dealer_name: conn.dealer_name,
        business_name: conn.company_name || '',
        city: conn.city,
        state: conn.state,
        country: conn.country
      }));
      setDealers(connectedDealers);
    } catch (error) {
      console.error('Error fetching dealers:', error);
      setDealers([]);
    }
  };

  const handleNewMessageClick = () => {
    fetchDealers();
    setShowNewMessageModal(true);
  };

  const handleStartConversation = (dealer) => {
    navigate(`/messages?userId=${dealer.id}&userName=${encodeURIComponent(dealer.dealer_name)}&businessName=${encodeURIComponent(dealer.business_name || '')}`);
    setShowNewMessageModal(false);
    setSearchDealer('');
    // Reload page to trigger URL params handling
    window.location.reload();
  };

  const filteredDealers = dealers.filter(dealer =>
    dealer.dealer_name.toLowerCase().includes(searchDealer.toLowerCase()) ||
    (dealer.business_name && dealer.business_name.toLowerCase().includes(searchDealer.toLowerCase()))
  );

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const hours = diff / (1000 * 60 * 60);

    if (hours < 24) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (hours < 168) {
      return date.toLocaleDateString('en-US', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-900 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <MessageCircle className="w-8 h-8 text-slate-900" />
            Messages
          </h1>
          <p className="text-gray-600 mt-1">Communicate with other dealers</p>
        </div>
        <button
          onClick={handleNewMessageClick}
          className="px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all flex items-center gap-2 whitespace-nowrap"
        >
          <Plus className="w-5 h-5" />
          <span>New Message</span>
        </button>
      </div>

      {/* Messages Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
        {/* Conversations List */}
        <div className="lg:col-span-1 bg-white rounded-3xl shadow-xl border-2 border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Conversations</h2>
          </div>
          <div className="overflow-y-auto h-full">
            {conversations.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Mail className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No conversations yet</p>
                <p className="text-sm mt-2">Start by messaging dealers from the Network page</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {conversations.map((conv) => (
                  <button
                    key={conv.user_id}
                    onClick={() => setSelectedConversation(conv)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      selectedConversation?.user_id === conv.user_id ? 'bg-slate-50 border-l-4 border-slate-900' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white font-bold">
                          {conv.user_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">{conv.user_name}</div>
                          {conv.user_business_name && (
                            <div className="text-xs text-gray-500">{conv.user_business_name}</div>
                          )}
                        </div>
                      </div>
                      {conv.unread_count > 0 && (
                        <div className="bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                          {conv.unread_count}
                        </div>
                      )}
                    </div>
                    <p className={`text-sm truncate ${conv.unread_count > 0 ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                      {conv.is_last_message_from_me ? 'You: ' : ''}
                      {conv.last_message}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                      <Clock className="w-3 h-3" />
                      {formatTime(conv.last_message_time)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Message Thread */}
        <div className="lg:col-span-2 bg-white rounded-3xl shadow-xl border-2 border-gray-100 overflow-hidden flex flex-col">
          {selectedConversation ? (
            <>
              {/* Thread Header */}
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white font-bold text-lg">
                      {selectedConversation.user_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-bold text-gray-900 text-lg">{selectedConversation.user_name}</div>
                      {selectedConversation.user_business_name && (
                        <div className="text-sm text-gray-600">{selectedConversation.user_business_name}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.map((msg) => {
                  const isFromMe = msg.sender_id !== selectedConversation.user_id;
                  return (
                    <div key={msg.id} className={`flex ${isFromMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] ${isFromMe ? 'order-2' : 'order-1'}`}>
                        <div
                          className={`rounded-2xl p-4 ${
                            isFromMe
                              ? 'bg-gradient-to-r from-slate-900 to-slate-700 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{msg.message}</p>
                        </div>
                        <div className={`flex items-center gap-1 text-xs text-gray-500 mt-1 ${isFromMe ? 'justify-end' : 'justify-start'}`}>
                          {isFromMe && msg.is_read && <CheckCheck className="w-3 h-3 text-blue-500" />}
                          <Clock className="w-3 h-3" />
                          {formatTime(msg.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex gap-4">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-500 focus:outline-none resize-none"
                    rows="2"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    className="px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Send className="w-5 h-5" />
                    {sending ? 'Sending...' : 'Send'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">Press Enter to send, Shift+Enter for new line</p>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <MessageCircle className="w-24 h-24 mx-auto mb-4 text-gray-300" />
                <p className="text-lg">Select a conversation to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Message Modal */}
      {showNewMessageModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setShowNewMessageModal(false);
            setSearchDealer('');
          }}
        >
          <div
            className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Users className="w-8 h-8" />
                New Message
              </h2>
              <button
                onClick={() => {
                  setShowNewMessageModal(false);
                  setSearchDealer('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Search */}
              <input
                type="text"
                placeholder="Search dealers..."
                value={searchDealer}
                onChange={(e) => setSearchDealer(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-900 focus:outline-none"
              />

              {/* Dealers List */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {filteredDealers.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="font-semibold text-gray-900 mb-2">
                      {dealers.length === 0 ? 'No Connections Yet' : 'No dealers found'}
                    </p>
                    {dealers.length === 0 ? (
                      <p className="text-sm text-gray-600 max-w-sm mx-auto">
                        Connect with other dealers from the <strong>Network</strong> page first, then you can message them here.
                      </p>
                    ) : (
                      <p className="text-sm text-gray-600">Try a different search term</p>
                    )}
                  </div>
                ) : (
                  filteredDealers.map((dealer) => (
                    <button
                      key={dealer.id}
                      onClick={() => handleStartConversation(dealer)}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-slate-300 hover:shadow-md transition-all text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white font-bold text-lg">
                          {dealer.dealer_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">{dealer.dealer_name}</div>
                          {dealer.business_name && (
                            <div className="text-sm text-gray-600">{dealer.business_name}</div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
